import React, { useEffect, useRef } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { AlertTriangle, Loader2, Lock, X } from 'lucide-react';
import { useIdioma } from '../../i18n/idioma';

interface ModalAccesoProps {
    onClose: () => void;
    onLogin: () => void;
    loading: boolean;
    error: string | null;
}

/**
 * Acceso con Google.
 *
 * Poco diseño y mucha mecánica a propósito: es el último paso antes de entrar
 * y lo único que importa es que no falle. Escape cierra, el clic fuera cierra,
 * el foco entra al diálogo y vuelve al botón que lo abrió, y el fondo no se
 * desplaza debajo.
 */
const ModalAcceso: React.FC<ModalAccesoProps> = ({ onClose, onLogin, loading, error }) => {
    const { t } = useIdioma();
    const panelRef = useRef<HTMLDivElement>(null);
    const primeroRef = useRef<HTMLButtonElement>(null);

    /* En el CUERPO del componente, NO dentro del efecto de abajo. Ver la nota
       igual en ModalIdioma: un hook llamado desde el callback de un `useEffect`
       no registra nada, así que el bloqueo no llegaba a aplicarse.

       Aquí además tiraba la app entera con «Invalid hook call» en cuanto se
       pulsaba «Empieza gratis» — y este es el último paso antes de entrar, así
       que nadie podía iniciar sesión. */
    useBodyScrollLock();

    useEffect(() => {
        const previo = document.activeElement as HTMLElement | null;
        /* El bloqueo lo lleva `useBodyScrollLock` (abajo), que cuenta capas.
           Guardar y restaurar aquí dejaba la página trabada si esta capa se
           cerraba después de otra que también hubiera bloqueado. */
        primeroRef.current?.focus();

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;

            // Retención del foco: sin esto el tabulador se escapa al fondo y el
            // usuario de teclado se pierde detrás del diálogo.
            const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), a[href], input, [tabindex]:not([tabindex="-1"])',
            );
            if (!focusables?.length) return;
            const primero = focusables[0];
            const ultimo = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === primero) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primero.focus();
            }
        };

        window.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('keydown', onKey);
            previo?.focus?.();
        };
    }, [onClose]);

    return (
        <div className="rp rp-on-light fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-navy-950/70"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="titulo-acceso"
                className="relative w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-s3"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('lp.acceso.cerrar')}
                    className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-lg text-white/60 transition-colors hover:text-white"
                >
                    <X className="h-5 w-5" aria-hidden="true" />
                </button>

                <div className="flex flex-col items-center bg-navy-850 px-8 py-10">
                    <img src="/logo.webp" alt="" className="h-14 w-auto object-contain" />
                    <p className="mt-3 text-[13px] font-semibold tracking-tight text-brand-400">
                        Raza Pro
                    </p>
                </div>

                <div className="px-8 py-9">
                    <h2
                        id="titulo-acceso"
                        className="text-center text-[22px] font-bold tracking-tight text-navy-950"
                    >
                        {t('lp.acceso.bienvenido')}
                    </h2>
                    <p className="mt-2 text-center text-[15px] text-acero-700">
                        {t('lp.acceso.sub')}
                    </p>

                    {error && (
                        <div
                            role="alert"
                            className="mt-6 flex items-start gap-2 rounded-xl bg-peligro-bg p-4 text-[14px] font-medium text-peligro"
                        >
                            <AlertTriangle className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                            <span>{error}</span>
                        </div>
                    )}

                    <button
                        ref={primeroRef}
                        type="button"
                        onClick={onLogin}
                        disabled={loading}
                        className="mt-7 flex w-full items-center justify-center gap-3 rounded-xl border border-acero-300 bg-white px-6 py-4 text-[15px] font-semibold text-acero-800 transition-colors hover:border-navy-850 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {loading ? (
                            <Loader2 className="h-5 w-5 animate-spin text-brand-500" aria-hidden="true" />
                        ) : (
                            <img
                                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                alt=""
                                className="h-5 w-5"
                            />
                        )}
                        {loading ? t('lp.acceso.verificando') : t('lp.acceso.google')}
                    </button>
                </div>

                <p className="flex items-center justify-center gap-1.5 border-t border-acero-200 bg-acero-50 py-4 text-[13px] font-medium text-acero-600">
                    <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('lp.acceso.seguro')}
                </p>
            </div>
        </div>
    );
};

export default ModalAcceso;
